import { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { settingsService } from "../services/settingsService";
import {
  FileText,
  Mail,
  Phone,
  MapPin,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Image,
  Link as LinkIcon,
  MessageSquare,
  Plus,
  Trash2,
  Settings,
  BarChart3,
  Upload,
  MessageSquareQuote,
} from "lucide-react";
import { uploadImage } from "../services/uploadService";

interface ContentSection {
  title: string;
  content: string;
  image?: string;
  lastUpdated?: string;
}

interface ContactInfo {
  email: string;
  phone: string;
  address: string;
  supportEmail: string;
  socialMedia: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
    youtube?: string;
  };
}

interface FAQ {
  _id?: string;
  question?: string;
  answer?: string;
  order?: number;
  isActive?: boolean;
}

interface SiteSettings {
  siteName: string;
  siteDescription: string;
  logo: string;
  favicon: string;
  maintenanceMode: boolean;
  maintenanceMessage: string;
  deviceLimit: number;
}

interface HeroSection {
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
  backgroundImage: string;
}

interface StatsSection {
  title: string;
  students: number;
  courses: number;
  countries: number;
}

interface Testimonial {
  _id?: string;
  author: string;
  role?: string;
  content: string;
  image: string;
  rating: number;
  order: number;
  isActive: boolean;
}

type TabType =
  | "about"
  | "mission"
  | "policies"
  | "contact"
  | "faq"
  | "hero"
  | "stats"
  | "site"
  | "reviews";

export default function PagesPage() {
  const [activeTab, setActiveTab] = useState<TabType>("about");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"success" | "error" | null>(
    null,
  );
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  // Content states
  const [aboutUs, setAboutUs] = useState<ContentSection>({
    title: "",
    content: "",
    image: "",
  });
  const [mission, setMission] = useState<ContentSection>({
    title: "",
    content: "",
  });
  const [termsOfService, setTermsOfService] = useState<ContentSection>({
    title: "",
    content: "",
  });
  const [privacyPolicy, setPrivacyPolicy] = useState<ContentSection>({
    title: "",
    content: "",
  });
  const [refundPolicy, setRefundPolicy] = useState<ContentSection>({
    title: "",
    content: "",
  });
  const [contact, setContact] = useState<ContactInfo>({
    email: "",
    phone: "",
    address: "",
    supportEmail: "",
    socialMedia: {
      facebook: "",
      twitter: "",
      linkedin: "",
      instagram: "",
      youtube: "",
    },
  });
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [hero, setHero] = useState<HeroSection>({
    title: "",
    subtitle: "",
    ctaText: "",
    ctaLink: "",
    backgroundImage: "",
  });
  const [site, setSite] = useState<SiteSettings>({
    siteName: "",
    siteDescription: "",
    logo: "",
    favicon: "",
    maintenanceMode: false,
    maintenanceMessage: "",
    deviceLimit: 2,
  });
  const [stats, setStats] = useState<StatsSection>({
    title: "",
    students: 120,
    courses: 16,
    countries: 56,
  });
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setIsLoading(true);
    try {
      const response = await settingsService.getContent();
      const content: any = response.content || response;

      if (content.aboutUs) setAboutUs(content.aboutUs);
      if (content.mission) setMission(content.mission);
      if (content.termsOfService) setTermsOfService(content.termsOfService);
      if (content.privacyPolicy) setPrivacyPolicy(content.privacyPolicy);
      if (content.refundPolicy) setRefundPolicy(content.refundPolicy);
      if (content.contact) {
        setContact({
          ...content.contact,
          socialMedia: content.contact.socialMedia || {
            facebook: "",
            twitter: "",
            linkedin: "",
            instagram: "",
            youtube: "",
          },
        });
      }
      if (content.faq) setFaqs(content.faq);
      if (content.hero) setHero(content.hero);
      if (content.stats) setStats(content.stats);
      if (content.site) setSite(content.site);
      if (content.testimonials) setTestimonials(content.testimonials);
    } catch (error) {
      console.error("Failed to fetch content:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (section: string, data: any) => {
    setIsSaving(true);
    setSaveStatus(null);
    setSaveErrorMessage(null);
    try {
      // Ensure socialMedia is never undefined for contact section
      if (section === "contact") {
        const contactData = {
          ...data,
          socialMedia: data.socialMedia || {
            facebook: "",
            twitter: "",
            linkedin: "",
            instagram: "",
            youtube: "",
          },
        };
        await settingsService.updateSection(section, contactData);
      } else {
        await settingsService.updateSection(section, data);
      }
      setSaveStatus("success");
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error: any) {
      console.error("Failed to save:", error);
      const msg =
        error?.message ||
        error?.data?.message ||
        "Failed to save changes. Please try again.";
      setSaveErrorMessage(msg);
      setSaveStatus("error");
    } finally {
      setIsSaving(false);
    }
  };

  const addFAQ = () => {
    setFaqs([
      ...faqs,
      {
        question: "",
        answer: "",
        order: faqs.length,
        isActive: true,
      },
    ]);
  };

  const removeFAQ = (index: number) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  const updateFAQ = (index: number, field: keyof FAQ, value: any) => {
    const updated = [...faqs];
    updated[index] = { ...updated[index], [field]: value };
    setFaqs(updated);
  };

  const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    setIsUploading(true);
    try {
      const files = Array.from(e.target.files);
      const uploadPromises = files.map((file) =>
        uploadImage(file, "testimonials"),
      );
      const urls = await Promise.all(uploadPromises);

      const newTestimonials = urls.map((url, index) => ({
        author: "New Reviewer",
        role: "Student",
        content: "Great course!",
        image: url || "",
        rating: 5,
        isActive: true,
        order: testimonials.length + index,
      }));

      const response = await settingsService.addTestimonials(newTestimonials);
      if (response.testimonials) {
        // Merge properly, response.testimonials are the NEW ones.
        // But to be safe and get IDs, let's refetch or append.
        // The backend returns the newly created ones.
        setTestimonials([
          ...testimonials,
          ...(response.testimonials as Testimonial[]),
        ]);
        setSaveStatus("success");
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (error) {
      console.error("Bulk upload failed:", error);
      setSaveStatus("error");
    } finally {
      setIsUploading(false);
      // Reset input
      e.target.value = "";
    }
  };

  const updateTestimonial = async (
    index: number,
    field: keyof Testimonial,
    value: any,
  ) => {
    const updated = [...testimonials];
    const testimonial = updated[index];
    if (!testimonial) return;

    updated[index] = { ...testimonial, [field]: value };
    setTestimonials(updated);

    // Auto-save individual updates (debounced ideally, but direct for now)
    if (testimonial._id) {
      try {
        await settingsService.updateTestimonial(testimonial._id, {
          [field]: value,
        });
      } catch (error) {
        console.error("Failed to update testimonial", error);
      }
    }
  };

  const deleteTestimonial = async (index: number) => {
    const testimonial = testimonials[index];
    if (!testimonial) return;

    if (testimonial._id) {
      if (!window.confirm("Are you sure you want to delete this review?"))
        return;
      try {
        await settingsService.deleteTestimonial(testimonial._id);
        setTestimonials(testimonials.filter((_, i) => i !== index));
      } catch (error) {
        console.error("Failed to delete", error);
      }
    } else {
      setTestimonials(testimonials.filter((_, i) => i !== index));
    }
  };

  const addTestimonial = () => {
    setTestimonials([
      ...testimonials,
      {
        author: "Student Name",
        role: "Student",
        content: "Review content...",
        image: "", // Placeholder or require upload
        rating: 5,
        order: testimonials.length,
        isActive: true,
      },
    ]);
  };

  const tabs = [
    { id: "about", label: "About Us", icon: FileText },
    { id: "mission", label: "Mission", icon: FileText },
    { id: "policies", label: "Policies", icon: FileText },
    { id: "contact", label: "Contact", icon: Mail },
    { id: "faq", label: "FAQ", icon: MessageSquare },
    { id: "hero", label: "Hero Section", icon: Image },
    { id: "reviews", label: "Reviews", icon: MessageSquareQuote },
    { id: "stats", label: "Statistics", icon: BarChart3 },
    { id: "site", label: "Site Settings", icon: Settings },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Content Management
          </h1>
          <p className="text-gray-500 mt-1">
            Manage your website content, policies, and settings
          </p>
        </div>

        {/* Save Status */}
        {saveStatus && (
          <div
            className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              saveStatus === "success"
                ? "bg-green-50 text-green-600"
                : "bg-red-50 text-red-600"
            }`}
          >
            {saveStatus === "success" ? (
              <CheckCircle size={16} />
            ) : (
              <AlertCircle size={16} />
            )}
            <span className="text-sm">
              {saveStatus === "success"
                ? "Changes saved successfully!"
                : saveErrorMessage ||
                  "Failed to save changes. Please try again."}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? "bg-[#102040] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* About Us Tab */}
        {activeTab === "about" && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">About Us Section</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <Input
                  value={aboutUs.title}
                  onChange={(e) =>
                    setAboutUs({ ...aboutUs, title: e.target.value })
                  }
                  placeholder="About Us"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content
                </label>
                <textarea
                  value={aboutUs.content}
                  onChange={(e) =>
                    setAboutUs({ ...aboutUs, content: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 min-h-[200px]"
                  placeholder="Write about your platform..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL
                </label>
                <Input
                  value={aboutUs.image}
                  onChange={(e) =>
                    setAboutUs({ ...aboutUs, image: e.target.value })
                  }
                  placeholder="https://..."
                  icon={<Image size={16} />}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave("aboutUs", aboutUs)}
                  disabled={isSaving}
                >
                  <Save size={16} className="mr-2" />
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Mission Tab */}
        {activeTab === "mission" && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Mission Section</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <Input
                  value={mission.title}
                  onChange={(e) =>
                    setMission({ ...mission, title: e.target.value })
                  }
                  placeholder="Our Mission"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content
                </label>
                <textarea
                  value={mission.content}
                  onChange={(e) =>
                    setMission({ ...mission, content: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 min-h-[150px]"
                  placeholder="Write your mission statement..."
                />
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave("mission", mission)}
                  disabled={isSaving}
                >
                  <Save size={16} className="mr-2" />
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Policies Tab */}
        {activeTab === "policies" && (
          <div className="space-y-6">
            {/* Terms of Service */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Terms of Service</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <Input
                    value={termsOfService.title}
                    onChange={(e) =>
                      setTermsOfService({
                        ...termsOfService,
                        title: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content
                  </label>
                  <textarea
                    value={termsOfService.content}
                    onChange={(e) =>
                      setTermsOfService({
                        ...termsOfService,
                        content: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 min-h-[150px]"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    onClick={() => handleSave("termsOfService", termsOfService)}
                    disabled={isSaving}
                  >
                    <Save size={16} className="mr-2" />
                    Save
                  </Button>
                </div>
              </div>
            </Card>

            {/* Privacy Policy */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Privacy Policy</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <Input
                    value={privacyPolicy.title}
                    onChange={(e) =>
                      setPrivacyPolicy({
                        ...privacyPolicy,
                        title: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content
                  </label>
                  <textarea
                    value={privacyPolicy.content}
                    onChange={(e) =>
                      setPrivacyPolicy({
                        ...privacyPolicy,
                        content: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 min-h-[150px]"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    onClick={() => handleSave("privacyPolicy", privacyPolicy)}
                    disabled={isSaving}
                  >
                    <Save size={16} className="mr-2" />
                    Save
                  </Button>
                </div>
              </div>
            </Card>

            {/* Refund Policy */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Refund Policy</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <Input
                    value={refundPolicy.title}
                    onChange={(e) =>
                      setRefundPolicy({
                        ...refundPolicy,
                        title: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content
                  </label>
                  <textarea
                    value={refundPolicy.content}
                    onChange={(e) =>
                      setRefundPolicy({
                        ...refundPolicy,
                        content: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 min-h-[150px]"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    onClick={() => handleSave("refundPolicy", refundPolicy)}
                    disabled={isSaving}
                  >
                    <Save size={16} className="mr-2" />
                    Save
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === "contact" && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <Input
                    value={contact.email}
                    onChange={(e) =>
                      setContact({ ...contact, email: e.target.value })
                    }
                    icon={<Mail size={16} />}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Support Email
                  </label>
                  <Input
                    value={contact.supportEmail}
                    onChange={(e) =>
                      setContact({ ...contact, supportEmail: e.target.value })
                    }
                    icon={<Mail size={16} />}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <Input
                  value={contact.phone}
                  onChange={(e) =>
                    setContact({ ...contact, phone: e.target.value })
                  }
                  icon={<Phone size={16} />}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <Input
                  value={contact.address}
                  onChange={(e) =>
                    setContact({ ...contact, address: e.target.value })
                  }
                  icon={<MapPin size={16} />}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <h4 className="text-md font-medium text-blue-900 mb-2">
                  Social Media Links
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  Social media links are now managed separately for better
                  organization and analytics.
                </p>
                <Button
                  variant="outline"
                  onClick={() => (window.location.href = "/social-links")}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <LinkIcon size={16} className="mr-2" />
                  Manage Social Links
                </Button>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  variant="primary"
                  onClick={() => handleSave("contact", contact)}
                  disabled={isSaving}
                >
                  <Save size={16} className="mr-2" />
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* FAQ Tab */}
        {activeTab === "faq" && (
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">FAQ Management</h3>
              <Button variant="outline" onClick={addFAQ}>
                <Plus size={16} className="mr-2" />
                Add FAQ
              </Button>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={faq._id || index}
                  className="p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm font-medium text-gray-500">
                      FAQ #{index + 1}
                    </span>
                    <button
                      onClick={() => removeFAQ(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <Input
                      value={faq.question}
                      onChange={(e) =>
                        updateFAQ(index, "question", e.target.value)
                      }
                      placeholder="Question"
                    />
                    <textarea
                      value={faq.answer}
                      onChange={(e) =>
                        updateFAQ(index, "answer", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                      rows={3}
                      placeholder="Answer"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`faq-active-${index}`}
                        checked={faq.isActive}
                        onChange={(e) =>
                          updateFAQ(index, "isActive", e.target.checked)
                        }
                        className="rounded border-gray-300"
                      />
                      <label
                        htmlFor={`faq-active-${index}`}
                        className="text-sm text-gray-600"
                      >
                        Active
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              {faqs.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  No FAQs added yet. Click "Add FAQ" to create one.
                </p>
              )}
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave("faq", faqs)}
                  disabled={isSaving}
                >
                  <Save size={16} className="mr-2" />
                  {isSaving ? "Saving..." : "Save All FAQs"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Hero Section Tab */}
        {activeTab === "hero" && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Hero Section</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <Input
                  value={hero.title}
                  onChange={(e) => setHero({ ...hero, title: e.target.value })}
                  placeholder="Welcome to Our Platform"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtitle
                </label>
                <Input
                  value={hero.subtitle}
                  onChange={(e) =>
                    setHero({ ...hero, subtitle: e.target.value })
                  }
                  placeholder="Learn from the best courses"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CTA Button Text
                  </label>
                  <Input
                    value={hero.ctaText}
                    onChange={(e) =>
                      setHero({ ...hero, ctaText: e.target.value })
                    }
                    placeholder="Get Started"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CTA Button Link
                  </label>
                  <Input
                    value={hero.ctaLink}
                    onChange={(e) =>
                      setHero({ ...hero, ctaLink: e.target.value })
                    }
                    placeholder="/courses"
                    icon={<LinkIcon size={16} />}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Background Image URL
                </label>
                <Input
                  value={hero.backgroundImage}
                  onChange={(e) =>
                    setHero({ ...hero, backgroundImage: e.target.value })
                  }
                  placeholder="https://..."
                  icon={<Image size={16} />}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave("hero", hero)}
                  disabled={isSaving}
                >
                  <Save size={16} className="mr-2" />
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Statistics Tab */}
        {activeTab === "stats" && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Homepage Statistics</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Title
                </label>
                <Input
                  value={stats.title}
                  onChange={(e) =>
                    setStats({ ...stats, title: e.target.value })
                  }
                  placeholder="Numbers Speak Louder Than Words!"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Student Count
                  </label>
                  <Input
                    type="number"
                    value={stats.students}
                    onChange={(e) =>
                      setStats({
                        ...stats,
                        students: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="120"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course Count
                  </label>
                  <Input
                    type="number"
                    value={stats.courses}
                    onChange={(e) =>
                      setStats({
                        ...stats,
                        courses: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="16"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country Count
                  </label>
                  <Input
                    type="number"
                    value={stats.countries}
                    onChange={(e) =>
                      setStats({
                        ...stats,
                        countries: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="56"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave("stats", stats)}
                  disabled={isSaving}
                >
                  <Save size={16} className="mr-2" />
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Site Settings Tab */}
        {activeTab === "site" && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Site Settings</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Name
                  </label>
                  <Input
                    value={site.siteName}
                    onChange={(e) =>
                      setSite({ ...site, siteName: e.target.value })
                    }
                    placeholder="My Platform"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Description
                  </label>
                  <Input
                    value={site.siteDescription}
                    onChange={(e) =>
                      setSite({ ...site, siteDescription: e.target.value })
                    }
                    placeholder="Online learning platform"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo URL
                  </label>
                  <Input
                    value={site.logo}
                    onChange={(e) => setSite({ ...site, logo: e.target.value })}
                    placeholder="https://..."
                    icon={<Image size={16} />}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Favicon URL
                  </label>
                  <Input
                    value={site.favicon}
                    onChange={(e) =>
                      setSite({ ...site, favicon: e.target.value })
                    }
                    placeholder="https://..."
                    icon={<Image size={16} />}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Global Device Limit
                  </label>
                  <div className="flex flex-col gap-1">
                    <Input
                      type="number"
                      min="1"
                      value={site.deviceLimit}
                      onChange={(e) =>
                        setSite({
                          ...site,
                          deviceLimit: parseInt(e.target.value) || 2,
                        })
                      }
                      placeholder="2"
                    />
                    <span className="text-xs text-gray-500">
                      Maximum number of active devices allowed per user
                      (default: 2)
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="maintenanceMode"
                    checked={site.maintenanceMode}
                    onChange={(e) =>
                      setSite({ ...site, maintenanceMode: e.target.checked })
                    }
                    className="rounded border-gray-300"
                  />
                  <label
                    htmlFor="maintenanceMode"
                    className="text-sm font-medium text-amber-800"
                  >
                    Maintenance Mode
                  </label>
                </div>
                <p className="text-xs text-amber-700 mb-2">
                  When enabled, visitors will see a maintenance message instead
                  of the site.
                </p>
                <textarea
                  value={site.maintenanceMessage}
                  onChange={(e) =>
                    setSite({ ...site, maintenanceMessage: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white"
                  rows={2}
                  placeholder="Maintenance message..."
                />
              </div>

              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={() => handleSave("site", site)}
                  disabled={isSaving}
                >
                  <Save size={16} className="mr-2" />
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold">
                  Testimonials & Reviews
                </h3>
                <p className="text-sm text-gray-500">
                  Manage student success stories displayed on the home page
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={addTestimonial}>
                  <Plus size={16} className="mr-2" />
                  Add Manual
                </Button>
                <div className="relative">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleBulkUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={isUploading}
                  />
                  <Button variant="primary" disabled={isUploading}>
                    <Upload size={16} className="mr-2" />
                    {isUploading ? "Uploading..." : "Bulk Upload Images"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Drag & Drop Zone Hint */}
            <div
              className={`border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-8 bg-gray-50 transition-colors ${
                isUploading
                  ? "bg-blue-50 border-blue-400"
                  : "hover:border-blue-400"
              }`}
            >
              <div className="relative">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleBulkUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isUploading}
                />
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                <p className="text-sm text-gray-600 font-medium">
                  {isUploading
                    ? "Uploading your images..."
                    : "Drag & drop review screenshots here to bulk create"}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Supports multiple images at once
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((review, index) => (
                <div
                  key={review._id || index}
                  className="border rounded-lg p-4 bg-white shadow-sm relative group"
                >
                  <button
                    onClick={() => deleteTestimonial(index)}
                    className="absolute top-2 right-2 p-1 bg-red-50 text-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={16} />
                  </button>

                  <div className="mb-4 aspect-4/3 bg-gray-100 rounded-md overflow-hidden flex items-center justify-center">
                    {review.image ? (
                      <img
                        src={review.image}
                        alt={review.author}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Image className="text-gray-300" size={32} />
                    )}
                  </div>

                  <div className="space-y-3">
                    <Input
                      value={review.author}
                      onChange={(e) =>
                        updateTestimonial(index, "author", e.target.value)
                      }
                      placeholder="Student Name"
                      className="font-medium"
                    />
                    <Input
                      value={review.role || ""}
                      onChange={(e) =>
                        updateTestimonial(index, "role", e.target.value)
                      }
                      placeholder="Role (e.g. PMP Student)"
                      className="text-sm"
                    />
                    <textarea
                      value={review.content}
                      onChange={(e) =>
                        updateTestimonial(index, "content", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-y min-h-[80px]"
                      placeholder="Review content..."
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Rating:</span>
                      <select
                        value={review.rating}
                        onChange={(e) =>
                          updateTestimonial(
                            index,
                            "rating",
                            parseInt(e.target.value),
                          )
                        }
                        className="border border-gray-200 rounded px-2 py-1 text-sm"
                      >
                        {[5, 4, 3, 2, 1].map((r) => (
                          <option key={r} value={r}>
                            {r} Stars
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t mt-2">
                      <input
                        type="checkbox"
                        checked={review.isActive}
                        onChange={(e) =>
                          updateTestimonial(index, "isActive", e.target.checked)
                        }
                        className="rounded border-gray-300"
                      />
                      <span className="text-xs text-gray-500">
                        Visible on site
                      </span>
                    </div>
                  </div>

                  {!review._id && (
                    <Button
                      size="sm"
                      className="w-full mt-3"
                      onClick={async () => {
                        try {
                          const res =
                            await settingsService.addTestimonial(review);
                          // Update with new ID
                          const updated = [...testimonials];
                          updated[index] = res.testimonial as Testimonial;
                          setTestimonials(updated);
                          setSaveStatus("success");
                          setTimeout(() => setSaveStatus(null), 3000);
                        } catch (e) {
                          console.error(e);
                          setSaveStatus("error");
                        }
                      }}
                    >
                      <Save size={14} className="mr-1" /> Save Review
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
